/**
 * service-bundle-cards.js
 *
 * Fetches active service bundle cards for a category and renders them as
 * animated hero cards inside #service-bundle-hero[data-category="..."].
 *
 * Static site: AscendDraft-Website/js/components/service-bundle-cards.js
 * Loaded as:   <script type="module" src="...service-bundle-cards.js"></script>
 */

const HOST = window.location.hostname;
const IS_LOCAL =
  HOST === "localhost" ||
  HOST === "127.0.0.1" ||
  HOST === "" ||
  window.location.protocol === "file:";
const API_BASE = IS_LOCAL
  ? "http://localhost:5001/api"
  : "https://api.ascenddraft.com/api";

/** Map Tailwind gradient class strings to concrete hex pairs */
const ACCENT_GRADIENTS = {
  "from-violet-500 to-purple-700": ["#8b5cf6", "#6d28d9"],
  "from-blue-500 to-indigo-700": ["#3b82f6", "#4338ca"],
  "from-emerald-500 to-teal-700": ["#10b981", "#0f766e"],
  "from-rose-500 to-pink-700": ["#f43f5e", "#be185d"],
  "from-amber-400 to-orange-600": ["#fbbf24", "#ea580c"],
  "from-cyan-400 to-sky-600": ["#22d3ee", "#0284c7"],
};

function resolveAccent(accentColor) {
  return ACCENT_GRADIENTS[accentColor] || ["#8b5cf6", "#6d28d9"];
}

/** Sanitise a plain-text string before injecting as textContent (not innerHTML) */
function text(str) {
  return String(str ?? "");
}

/** Format a raw USD price in the user's detected/selected currency (never a
 * hardcoded symbol) — falls back to a plain $ only if CurrencyService hasn't
 * loaded yet. */
function fmtUSD(n) {
  return window.CurrencyService?.formatFromUSD
    ? window.CurrencyService.formatFromUSD(Number(n), { decimals: 0 })
    : `$${Number(n).toFixed(0)}`;
}

/** Build the card HTML for one bundle */
function renderCard(bundle, idx) {
  const card = document.createElement("article");
  card.className = "sbh-card";
  card.setAttribute("aria-label", text(bundle.name));
  // Stagger delay via inline style (mirrors the CSS nth-child rules with more precision)
  card.style.transitionDelay = `${idx * 80}ms`;

  // Apply accent colour CSS variables (used for top stripe, chip, bullet tick, CTA button)
  const [c1, c2] = resolveAccent(bundle.accentColor);
  card.style.setProperty("--sbh-c1", c1);
  card.style.setProperty("--sbh-c2", c2);

  // Tier label chip
  if (bundle.cardLabel) {
    const chip = document.createElement("span");
    chip.className = "sbh-chip-label";
    chip.textContent = text(bundle.cardLabel);
    card.appendChild(chip);
  }

  // Service tag chips
  if (bundle.serviceTags && bundle.serviceTags.length) {
    const tagsRow = document.createElement("div");
    tagsRow.className = "sbh-tags";
    bundle.serviceTags.forEach((tag) => {
      const t = document.createElement("span");
      t.className = "sbh-tag";
      t.textContent = text(tag);
      tagsRow.appendChild(t);
    });
    card.appendChild(tagsRow);
  }

  // Bullet points
  if (bundle.bullets && bundle.bullets.length) {
    const ul = document.createElement("ul");
    ul.className = "sbh-bullets";
    bundle.bullets.forEach((b) => {
      const li = document.createElement("li");
      li.textContent = text(b);
      ul.appendChild(li);
    });
    card.appendChild(ul);
  }

  // Footer — price + CTA
  const footer = document.createElement("div");
  footer.className = "sbh-footer";

  const priceRow = document.createElement("div");
  priceRow.className = "sbh-price";

  // Promo price: show original (struck) + badge when originalPrice > price
  const hasPromo =
    bundle.originalPrice &&
    Number(bundle.originalPrice) > Number(bundle.price) &&
    Number(bundle.price) > 0;

  if (hasPromo) {
    const origEl = document.createElement("span");
    origEl.className = "sbh-price-original";
    origEl.dataset.usd = bundle.originalPrice;
    origEl.textContent = fmtUSD(bundle.originalPrice);
    priceRow.appendChild(origEl);

    const savings = Math.round(
      ((bundle.originalPrice - bundle.price) / bundle.originalPrice) * 100,
    );
    const badge = document.createElement("span");
    badge.className = "sbh-price-promo-badge";
    badge.textContent = `${savings}% off`;
    priceRow.appendChild(badge);
  }

  const amount = document.createElement("span");
  amount.className = "sbh-price-amount";
  amount.dataset.usd = bundle.price;
  amount.textContent = fmtUSD(bundle.price);
  priceRow.appendChild(amount);

  if (bundle.priceLabel) {
    const lbl = document.createElement("span");
    lbl.className = "sbh-price-label";
    lbl.textContent = text(bundle.priceLabel);
    priceRow.appendChild(lbl);
  }
  footer.appendChild(priceRow);

  // ── Optional promo code row (collapsed by default) ───────────────────────
  const promoWrap = document.createElement("div");
  promoWrap.className = "sbh-promo-wrap";

  const promoToggle = document.createElement("button");
  promoToggle.type = "button";
  promoToggle.className = "sbh-promo-toggle";
  promoToggle.textContent = "Have a promo code?";
  promoWrap.appendChild(promoToggle);

  const promoRow = document.createElement("div");
  promoRow.className = "sbh-promo-row";
  promoRow.setAttribute("aria-hidden", "true");

  const promoInput = document.createElement("input");
  promoInput.type = "text";
  promoInput.className = "sbh-promo-input";
  promoInput.placeholder = "Enter code (optional)";
  promoInput.autocomplete = "off";
  promoInput.spellcheck = false;
  promoRow.appendChild(promoInput);
  promoWrap.appendChild(promoRow);
  footer.appendChild(promoWrap);

  promoToggle.addEventListener("click", () => {
    const open = promoRow.classList.toggle("sbh-promo-row--open");
    promoRow.setAttribute("aria-hidden", String(!open));
    promoToggle.classList.toggle("sbh-promo-toggle--active", open);
    if (open) promoInput.focus();
  });

  // ── CTA button ───────────────────────────────────────────────────────────
  // Adds the bundle to the basket and stops there — same pattern as every
  // individual service's "Add to basket" button. It used to clear the basket
  // and navigate straight to checkout in one synchronous burst, but that gave
  // a real write (which can race a transient identity/cross-tab hiccup) zero
  // margin to settle before the page unloaded, occasionally landing the item
  // under the wrong scoped key and leaving checkout showing stale data from
  // a previous session. Routing through the same "add, then the user
  // proceeds via the basket widget when ready" flow removes that race
  // entirely, rather than trying to make it resolve faster.
  const btn = document.createElement("button");
  btn.className = "sbh-cta-btn";
  btn.type = "button";
  const originalLabel = text(bundle.ctaLabel) || "Get Started";
  btn.textContent = originalLabel;
  btn.addEventListener("click", async () => {
    if (btn.disabled) return;
    btn.disabled = true;
    btn.textContent = "Adding…";

    const bundlePrice = Number(bundle.price);
    // Admin/API returns category in Title Case ("Academic"); every service
    // page (and checkout's own category <select>) uses lowercase ("academic").
    // Route through UnifiedBasket's shared normalizeCategory() (not a bare
    // .toLowerCase()) so a multi-word category like "Digital Automation" or
    // "Tutoring & Exam Prep" gets a stable, distinct slug instead of silently
    // failing every category comparison in unified-basket.js.
    const bundleCategory = window.UnifiedBasket?.normalizeCategory?.(bundle.category);
    if (!bundleCategory) {
      window.UnifiedBasket?.showToast?.(
        "Couldn't add this bundle. Please try again.",
        "error",
      );
      btn.disabled = false;
      btn.textContent = originalLabel;
      return;
    }
    const bundleItem = {
      id: `bundle_${bundle._id}`,
      name: bundle.name,
      price: bundlePrice,
      quantity: 1, // 'quantity' (not qty) matches unified-basket subtotal calc
      category: bundleCategory,
      type: "bundle",
      cardLabel: bundle.cardLabel || "",
      tags: bundle.serviceTags || [],
      originalPrice: bundle.originalPrice ? Number(bundle.originalPrice) : null,
    };

    let added = false;
    try {
      if (window.UnifiedBasket) {
        // No explicit clearBasket() — addItem()'s own single-item-category
        // logic already replaces an existing item of the same category
        // (matching how a same-category individual service replaces a
        // bundle, and vice versa), and correctly keeps different-category
        // items alongside each other for the cross-service combo flow.
        await window.UnifiedBasket.addItem(bundleItem);
        added = true;
      }
    } catch (_) {
      /* addItem failed — leave the button in an error-recoverable state below */
    }

    if (added) {
      btn.textContent = "✓ Added!";
      window.UnifiedBasket?.showToast?.(`${bundle.name} added to basket!`, "success");
      setTimeout(() => {
        btn.disabled = false;
        btn.textContent = originalLabel;
      }, 2000);
    } else {
      btn.disabled = false;
      btn.textContent = originalLabel;
    }
  });
  footer.appendChild(btn);

  card.appendChild(footer);
  return card;
}

/** Render skeleton placeholders while loading */
function renderSkeletons(container, count = 3) {
  for (let i = 0; i < count; i++) {
    const sk = document.createElement("div");
    sk.className = "sbh-skeleton";
    container.appendChild(sk);
  }
}

/** Main entry — called once per #service-bundle-hero element on the page */
async function initServiceBundleHero(root) {
  const rawCategory = root.getAttribute("data-category");
  if (!rawCategory) return;

  const section = document.createElement("section");
  section.className = "sbh-section";
  section.setAttribute("aria-label", `${rawCategory} bundle options`);

  const heading = document.createElement("h2");
  heading.innerHTML = `Popular <span>${rawCategory}</span> Bundles`;
  section.appendChild(heading);

  const intro = document.createElement("p");
  intro.className = "sbh-intro";
  intro.textContent =
    "Not happy with just one service, or want more than a DIY mix? These combos were put together with you in mind.";
  section.appendChild(intro);

  // Outer wrapper handles horizontal scroll; inner track handles centering
  const outer = document.createElement("div");
  outer.className = "sbh-track-outer";

  const track = document.createElement("div");
  track.className = "sbh-track";
  outer.appendChild(track);
  section.appendChild(outer);

  // Show skeletons immediately
  renderSkeletons(track, 3);
  root.appendChild(section);

  let bundles = [];
  try {
    const url = `${API_BASE}/public/service-bundles/${encodeURIComponent(rawCategory)}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    bundles = json.data || [];
  } catch {
    // Silently remove section if fetch fails — page works fine without bundles
    root.innerHTML = "";
    return;
  }

  if (!bundles.length) {
    root.innerHTML = "";
    return;
  }

  // Replace skeletons with real cards
  track.innerHTML = "";
  bundles.forEach((bundle, idx) => track.appendChild(renderCard(bundle, idx)));

  // Cards can render before OR after CurrencyService finishes detecting the
  // visitor's location/currency — the bundle-fetch above and currency
  // detection are two independent network calls racing each other. Neither
  // "refresh immediately" nor "listen for the currencyChanged event" alone
  // is reliable here: detection is often fast enough that its one-shot
  // event has already fired (and its promise already resolved) before this
  // line even runs, so a listener registered now never sees it, AND an
  // immediate synchronous refresh can itself run a tick before that
  // resolution lands. CurrencyService.init() returns the *same* promise on
  // every call (it does not re-trigger detection), so awaiting it here is
  // safe and cheap — it resolves instantly if detection already finished,
  // or waits for the in-flight detection to finish if it hasn't. Once
  // awaited, currency is guaranteed settled before the first refresh.
  const refreshBundlePrices = () => {
    track.querySelectorAll("[data-usd]").forEach((el) => {
      el.textContent = fmtUSD(el.dataset.usd);
    });
  };
  if (window.CurrencyService?.init) {
    await window.CurrencyService.init();
  }
  refreshBundlePrices();
  if (window.CurrencyService?.onCurrencyChange) {
    window.CurrencyService.onCurrencyChange(refreshBundlePrices);
  }

  // Animate cards in using IntersectionObserver
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.querySelectorAll(".sbh-card").forEach((card) => {
            card.classList.add("sbh-visible");
          });
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.1 },
  );
  observer.observe(section);
}

// Initialise all hero roots on page load
document
  .querySelectorAll("#service-bundle-hero")
  .forEach(initServiceBundleHero);
